import { Component, OnInit, Input, OnDestroy, ViewChild, AfterViewInit } from '@angular/core';
import { UntypedFormGroup, NgModel, Validators } from '@angular/forms';
import { RequirementField } from 'ng2-app/modules/borrower/models/application-requirements/application-requirements.interface';
import { SelectOption } from 'ng2-app/util/types/forms';
import { Subject, Observable, of, merge } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'ba-date',
    templateUrl: 'date-attribute.component.html',
    styleUrls: ['date-attribute.component.scss'],
    standalone: false
})
export class DateAttributeComponent implements OnInit, OnDestroy, AfterViewInit {

    @Input() field: RequirementField;
    @Input() form: UntypedFormGroup;
    @Input() type: 'date' | 'month' | 'datetime';
    @Input() disabled: boolean;

    @ViewChild('yearNgModel', { static: true }) yearNgModel: NgModel;

    day: string;
    dayOptions$: Observable<SelectOption[]>;
    dirty: boolean;
    maxYear = (new Date()).getFullYear();
    minYear = 1900;
    month: string;
    monthOptions = [
        { label: 'Jan', value: '01' },
        { label: 'Feb', value: '02' },
        { label: 'Mar', value: '03' },
        { label: 'Apr', value: '04' },
        { label: 'May', value: '05' },
        { label: 'Jun', value: '06' },
        { label: 'Jul', value: '07' },
        { label: 'Aug', value: '08' },
        { label: 'Sep', value: '09' },
        { label: 'Oct', value: '10' },
        { label: 'Nov', value: '11' },
        { label: 'Dec', value: '12' }
    ];
    showDay: boolean;
    year: string;
    yearMask = [/[1-2]/, /\d/, /\d/, /\d/];

    private destroyed$ = new Subject<void>();
    private month$ = new Subject<string>();

    ngOnInit() {
        this.showDay = this.type === 'month' ? false : true;

        if (this.showDay) {
            this.dayOptions$ = merge(of(null), this.month$).pipe(
                map((month) => {
                    const maxDay = (monthStr: string | null) => {
                        if (monthStr === null) {
                            return 31;
                        }
                        if (['01', '03', '05', '07', '08', '10', '12'].includes(monthStr)) {
                            return 31;
                        }
                        if (monthStr === '02') {
                            return 29;
                        }
                        return 30;
                    };

                    const max = maxDay(month);

                    const options: SelectOption[] = [];
                    // Add an empty option
                    options.push({ 'label': '', 'value': '0' });
                    for (let i = 1; i <= max; i++) {
                        let strVal = i.toString();
                        if (i < 10) {
                            strVal = `0${i}`;
                        }
                        options.push({ label: i.toString(), value: strVal });
                    }
                    return options;
                })
            );
        }

        // Set input values from the control
        if (this.control.value) {
            this.setDate(new Date(this.control.value));
        }

        // Subscribe to future changes to the control value
        this.control.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((newVal) => {

            // Ignore updates if the user has modified any input
            if (this.control.dirty) {
                return;
            }
            this.setDate(new Date(newVal));
        });
    }

    ngAfterViewInit() {
        this.yearNgModel.control.setValidators([Validators.min(this.minYear)]);
        this.checkCustomValidity();
    }

    ngOnDestroy() {
        this.destroyed$.next();
    }

    setDate(date: Date) {

        // Set the month
        let month = (date.getMonth() + 1).toString();
        if (month.length === 1) {
            month = '0' + month;
        }
        this.month = month;
        this.month$.next(month);

        // Set the day
        let day = date.getDate().toString();
        if (day.length === 1) {
            day = '0' + day;
        }
        this.day = day;

        // Set the year
        this.year = date.getFullYear().toString();
    }

    dayChanged() {
        this.updateControl();
        this.checkCustomValidity();
    }

    monthChanged(monthValue: string) {
        this.updateControl();
        this.month$.next(monthValue);
        this.checkCustomValidity();
    }

    yearChanged() {
        this.updateControl();
        this.control.setErrors({'yearInvalid': 'pending'});
        this.checkCustomValidity();
    }

    checkCustomValidity() {
        setTimeout(() => {
            const currentDate = Math.round((new Date()).getTime() / 1000);
            const attemptedDateString = this.year + '-' + this.month + '-' + this.day;
            const attemptedDate = Math.round((new Date(attemptedDateString)).getTime() / 1000);

            if (!this.yearNgModel.valid) {
                this.control.setErrors({'yearInvalid': true});
            } else if (
                this.yearNgModel.valid &&
                this.control.errors &&
                this.control.hasError('yearInvalid')
            ) {
                this.stripError('yearInvalid');
            }

            if (attemptedDate > currentDate) {
                this.control.setErrors({'futureDate': true});
            } else {
                this.stripError('futureDate');
            }
        });

    }

    stripError(errorToStrip) {
        let errors: object | null = Object.assign({}, this.control.errors);
        delete errors[errorToStrip];
        if (Object.keys(errors).length === 0) {
            errors = null;
        }
        this.control.setErrors(errors);
    }

    updateControl() {
        if (this.year === undefined || this.year.length < 4) {
            return;
        }
        if (this.month === undefined) {
            return;
        }

        let date = `${this.year}-${this.month}-`;
        if (this.type === 'date') {
            if (this.day === undefined || this.day === '0') {
                return;
            }
            const day = this.day.length === 1 ? '0' + this.day : this.day;
            date = date + day;
        } else if (this.type === 'month') {
            date = date + '01';
        }

        this.control.setValue(date);
        this.control.markAsDirty();
    }

    get control() {
        return this.form.controls[this.field.alias];
    }
}
